'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { Radio } from 'lucide-react'
import FormatList, { formats, FormatDetail } from './format-list'
import RecordingsSection from './recordings-section'

// Configuration variable for autoplay
const ENABLE_AUTOPLAY = false // Set to true to enable autoplay, false to disable

// Autoplay interval in milliseconds
const AUTOPLAY_INTERVAL = 5000 // 5 seconds

export default function FormatsSection() {
  const [activeFormat, setActiveFormat] = useState(0)
  const [isInView, setIsInView] = useState(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const sectionRef = useRef<HTMLDivElement>(null)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isActive, setIsActive] = useState(false)

  // Function to start the autoplay timer
  const startAutoplayTimer = useCallback(() => {
    // Clear any existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    // Only set a new interval if autoplay is enabled and the section is in view
    if (ENABLE_AUTOPLAY && isInView) {
      intervalRef.current = setInterval(() => {
        setActiveFormat((current) => (current + 1) % formats.length)
      }, AUTOPLAY_INTERVAL)
    }
  }, [isInView])

  // Function to change the active format
  const changeFormat = useCallback(
    (index: number) => {
      setActiveFormat(index)
      // Reset the timer when a format is manually selected (if autoplay is enabled)
      if (ENABLE_AUTOPLAY) {
        startAutoplayTimer()
      }
    },
    [startAutoplayTimer],
  )

  // Set up intersection observer to detect when the section is in view
  useEffect(() => {
    if (!sectionRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        setIsInView(entry.isIntersecting)
      },
      {
        // Adjust these values as needed
        threshold: 0.1, // 10% of the element is visible
        rootMargin: '0px',
      },
    )

    observer.observe(sectionRef.current)

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current)
      }
    }
  }, [])

  // Start or stop the autoplay timer based on visibility and configuration
  useEffect(() => {
    if (ENABLE_AUTOPLAY && isInView) {
      startAutoplayTimer()
    } else {
      // Clear the interval when not in view or autoplay is disabled
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    // Clean up the interval on component unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [isInView, startAutoplayTimer])

  useEffect(() => {
    document.body.style.overflow = 'auto'
  }, [isMobileMenuOpen])

  useEffect(() => {
    const currentSectionRef = sectionRef.current // Store ref value in a variable
    const handleScroll = () => {
      if (currentSectionRef) {
        const { top, bottom } = currentSectionRef.getBoundingClientRect()
        const windowHeight = window.innerHeight
        if (top < windowHeight * 0.7 && bottom > windowHeight * 0.3) {
          setIsActive(true)
        } else {
          setIsActive(false)
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll() // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, []) // Removed currentSectionRef from dependencies as it's captured

  const getBackgroundColor = (index: number) => {
    const colors = ['bg-blue-400', 'bg-green-400', 'bg-yellow-400', 'bg-purple-400']
    return colors[index % colors.length]
  }

  return (
    <section className="py-20 pt-12 bg-black/20 relative" ref={sectionRef}>
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 25px 25px, rgba(255, 255, 255, 0.2) 2%, transparent 0%)',
            backgroundSize: '50px 50px',
          }}
        ></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center mb-4">
              <div className="w-12 h-1 bg-radio-blue/30 rounded-full"></div>
              <Radio className="mx-4 text-radio-blue" />
              <div className="w-12 h-1 bg-radio-blue/30 rounded-full"></div>
            </div>
            <h2
              id="formats-section-title"
              className="text-3xl md:text-4xl font-display text-radio-blue mb-4"
            >
              Форматы Шоу
            </h2>
            <div className="w-24 h-1 bg-radio-blue/30 mx-auto rounded-full"></div>

            <p className="text-lg text-gray-300 max-w-3xl mx-auto mt-6">
              Вот чем здесь можно зарядиться:
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Format List (Left Side) */}
            <div className="w-full lg:w-1/2">
              <FormatList activeFormat={activeFormat} onFormatChange={changeFormat} />
            </div>

            {/* Format Image (Right Side) - Only visible on desktop */}
            <div className="w-full lg:w-1/2 hidden lg:block">
              {formats.map((format, index) => (
                <div
                  key={index}
                  role="tabpanel"
                  aria-labelledby={`format-${format.id}-tab`}
                  className={`transition-opacity duration-500 ${
                    activeFormat === index ? 'opacity-100' : 'opacity-0 hidden'
                  }`}
                >
                  <div className="relative">
                    <FormatDetail format={format} />

                    {/* Navigation buttons */}
                    <div className="absolute top-1/2 left-0 right-0 transform -translate-y-1/2 flex justify-between pointer-events-none px-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          changeFormat((activeFormat - 1 + formats.length) % formats.length)
                        }}
                        className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors pointer-events-auto"
                        aria-label="Previous format"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="w-5 h-5"
                        >
                          <path d="m15 18-6-6 6-6" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          changeFormat((activeFormat + 1) % formats.length)
                        }}
                        className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors pointer-events-auto"
                        aria-label="Next format"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="w-5 h-5"
                        >
                          <path d="m9 18 6-6-6-6" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recordings Section */}
          <div className="mt-16">
            <RecordingsSection />
          </div>
        </div>
      </div>
    </section>
  )
}
